<template>
    <div class="card config-section">
        <CardHeader
            title="Configurações de Notificação"
            icon="fas fa-envelope"
        />
        <div class="card-body">
            <div class="section-form">
                <!-- Alert informativo -->
                <div
                    class="alert alert-warning"
                    role="alert"
                >
                    <div class="alert-content">
                        <i class="fas fa-exclamation-triangle mr-2"></i>
                        <div>
                            <strong>Importante:</strong> Os emails devem ser separados por vírgula.
                                                         Certifique-se de que todos os endereços estejam corretos antes
                                                         de salvar.
                        </div>
                    </div>
                </div>

                <div class="row">
                    <!-- Campo de emails -->
                    <div class="col-md-12">
                        <div class="form-group">
                            <label
                                for="fleet_management_emails"
                                class="form-label"
                            >
                                <i class="fas fa-at mr-1"></i>
                                E-mails para notificações do sistema
                            </label>
                            <textarea
                                id="fleet_management_emails"
                                class="form-control"
                                :value="value.fleet_management_emails"
                                @input="updateField('fleet_management_emails', $event.target.value)"
                                placeholder="exemplo1@email.com, exemplo2@email.com, exemplo3@email.com"
                                rows="4"
                            ></textarea>
                            <small class="form-text text-muted">
                                <i class="fas fa-info-circle mr-1"></i>
                                Insira os emails separados por vírgula. Estes endereços receberão
                                notificações sobre reservas, cancelamentos e outras atividades do sistema.
                            </small>
                        </div>
                    </div>
                </div>

                <!-- Preview dos emails -->
                <div
                    class="email-preview"
                    v-if="emailList.length > 0"
                >
                    <div class="preview-header">
                        <i class="fas fa-eye mr-2"></i>
                        E-mails Configurados ({{ emailList.length }})
                    </div>
                    <div class="email-tags">
                        <span
                            v-for="(email, index) in emailList"
                            :key="index"
                            class="email-tag"
                            :class="{ 'invalid-email': !isValidEmail(email) }"
                        >
                            <i class="fas fa-envelope mr-1"></i>
                            {{ email.trim() }}
                            <i
                                v-if="!isValidEmail(email)"
                                class="fas fa-exclamation-triangle ml-1"
                                title="Email inválido"
                            ></i>
                        </span>
                    </div>

                    <!-- Contador de emails válidos/inválidos -->
                    <div class="email-summary">
                        <div class="summary-item valid">
                            <i class="fas fa-check-circle mr-1"></i>
                            {{ validEmailsCount }} válidos
                        </div>
                        <div
                            class="summary-item invalid"
                            v-if="invalidEmailsCount > 0"
                        >
                            <i class="fas fa-times-circle mr-1"></i>
                            {{ invalidEmailsCount }} inválidos
                        </div>
                    </div>
                </div>

                <!-- Estado vazio -->
                <div
                    class="empty-state"
                    v-else
                >
                    <div class="empty-icon">
                        <i class="fas fa-envelope-open"></i>
                    </div>
                    <p class="empty-text">
                        Nenhum email configurado para notificações
                    </p>
                    <small class="empty-subtext">
                        Digite os emails no campo acima para configurar as notificações
                    </small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CardHeader from "../../Base/CardHeader.vue";

export default {
    name: 'NotificationConfig',
    components: {CardHeader},
    props: {
        value: {
            type: Object,
            required: true
        },
    },
    computed: {
        emailList() {
            if (!this.value.fleet_management_emails) return [];
            return this.value.fleet_management_emails
                .split(',')
                .map(email => email.trim())
                .filter(email => email.length > 0);
        },

        validEmailsCount() {
            return this.emailList.filter(email => this.isValidEmail(email)).length;
        },

        invalidEmailsCount() {
            return this.emailList.filter(email => !this.isValidEmail(email)).length;
        }
    },
    methods: {
        updateField(field, newValue) {
            this.$emit('update', field, newValue);
        },

        isValidEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email.trim());
        }
    }
};
</script>

<style scoped>
.config-section {
    border: none;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    margin-bottom: 25px;
    overflow: hidden;
}

.card-header {
    background: linear-gradient(135deg, #6f42c1, #5a2d91);
    padding: 15px 20px;
    border-bottom: none;
}

.card-title {
    color: white;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.required-fields {
    font-size: 12px;
    opacity: 0.9;
}

.required {
    color: #ffeb3b;
    font-weight: bold;
}

.card-body {
    padding: 25px 20px;
    background-color: white;
}

.alert {
    border-radius: 8px;
    border: none;
    margin-bottom: 25px;
}

.alert-warning {
    background: linear-gradient(135deg, #fff3cd, #ffeaa7);
    color: #856404;
}

.alert-content {
    display: flex;
    align-items: flex-start;
}

.alert-content i {
    margin-top: 2px;
    font-size: 16px;
}

.form-group {
    margin-bottom: 20px;
}

.form-label {
    font-weight: 600;
    color: #5b5b5f;
    margin-bottom: 8px;
    display: block;
}

.form-control {
    padding: 12px 15px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    transition: all 0.3s ease;
    font-size: 14px;
    background-color: white;
    resize: vertical;
}

.form-control:focus {
    border-color: #6f42c1;
    box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.25);
    outline: none;
}

.form-control.is-invalid {
    border-color: #dc3545;
}

.invalid-feedback {
    display: block;
    color: #dc3545;
    font-size: 12px;
    margin-top: 5px;
}

.form-text {
    font-size: 12px;
    color: #6c757d;
    margin-top: 5px;
}

/* Email Preview */
.email-preview {
    margin-top: 25px;
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #dee2e6;
}

.preview-header {
    background: linear-gradient(135deg, #6f42c1, #5a2d91);
    color: white;
    padding: 12px 16px;
    font-weight: 600;
    font-size: 14px;
}

.email-tags {
    padding: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.email-tag {
    background: linear-gradient(135deg, #e3f2fd, #bbdefb);
    color: #0d47a1;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    display: flex;
    align-items: center;
    border: 1px solid #90caf9;
    transition: all 0.2s ease;
}

.email-tag.invalid-email {
    background: linear-gradient(135deg, #ffebee, #ffcdd2);
    color: #c62828;
    border-color: #ef5350;
}

.email-summary {
    padding: 12px 16px;
    border-top: 1px solid #dee2e6;
    display: flex;
    gap: 16px;
    background: rgba(255, 255, 255, 0.5);
}

.summary-item {
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
}

.summary-item.valid {
    color: #28a745;
}

.summary-item.invalid {
    color: #dc3545;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
}

.empty-icon {
    font-size: 48px;
    color: #dee2e6;
    margin-bottom: 16px;
}

.empty-text {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
    color: #495057;
}

.empty-subtext {
    font-size: 12px;
    color: #6c757d;
}

/* Responsividade */
@media (max-width: 768px) {
    .card-title {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .required-fields {
        align-self: flex-end;
    }

    .alert-content {
        flex-direction: column;
        gap: 8px;
    }

    .alert-content i {
        margin-top: 0;
    }

    .email-summary {
        flex-direction: column;
        gap: 8px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Parameters/components/NotificationConfig.vue